/**
 * MicroscopeSystem - Handles zoom and pan navigation
 * From PRD Section 2.3.1 and 6.2.1
 */

import Phaser from 'phaser';
import type { MicroscopeState, Vector2, StainType } from '@/types';
import { ZoomThreshold } from '@/types';

export interface MicroscopeConfig {
  /** Initial zoom level index */
  initialZoomIndex?: number;
  /** Bounds for panning (world coordinates) */
  worldBounds?: Phaser.Geom.Rectangle;
  /** Enable touch controls */
  touchEnabled?: boolean;
}

export interface ZoomEvent {
  level: number;
  index: number;
  direction: 'in' | 'out';
  threshold: ZoomThreshold;
}

export interface PanEvent {
  position: Vector2;
  delta: Vector2;
}

/**
 * MicroscopeSystem manages zoom levels and pan navigation
 * Emits events for UI updates and detail visibility changes
 */
export class MicroscopeSystem {
  private scene: Phaser.Scene;
  private state: MicroscopeState;
  private config: Required<MicroscopeConfig>;

  /** Standard microscope zoom levels */
  public static readonly ZOOM_LEVELS = [1, 10, 40, 100, 400, 1000] as const;

  /** Current zoom level index */
  private zoomIndex: number;

  /** Dragging state */
  private isDragging = false;
  private lastPointerPosition: Vector2 = { x: 0, y: 0 };

  /** Touch state for pinch zoom */
  private lastPinchDistance = 0;
  private isPinching = false;

  constructor(scene: Phaser.Scene, config: MicroscopeConfig = {}) {
    this.scene = scene;
    this.config = {
      initialZoomIndex: config.initialZoomIndex ?? 0,
      worldBounds: config.worldBounds ?? new Phaser.Geom.Rectangle(-1000, -1000, 2000, 2000),
      touchEnabled: config.touchEnabled ?? true,
    };

    this.zoomIndex = this.config.initialZoomIndex;

    this.state = {
      zoomLevel: MicroscopeSystem.ZOOM_LEVELS[this.zoomIndex],
      position: { x: 0, y: 0 },
      focusDepth: 0,
      stainType: 'none',
      scannerActive: false,
    };

    this.setupInputHandlers();
  }

  /**
   * Set up mouse and touch input handlers
   */
  private setupInputHandlers(): void {
    const input = this.scene.input;

    // Mouse wheel zoom
    input.on('wheel', this.handleWheel, this);

    // Mouse drag for pan
    input.on('pointerdown', this.handlePointerDown, this);
    input.on('pointermove', this.handlePointerMove, this);
    input.on('pointerup', this.handlePointerUp, this);
    input.on('pointerupoutside', this.handlePointerUp, this);

    // Keyboard zoom controls
    if (input.keyboard) {
      input.keyboard.on('keydown-PLUS', () => this.zoomIn());
      input.keyboard.on('keydown-MINUS', () => this.zoomOut());
      input.keyboard.on('keydown-EQUALS', () => this.zoomIn()); // = key (shift for +)
    }
  }

  /**
   * Handle mouse wheel for zoom
   */
  private handleWheel(_pointer: Phaser.Input.Pointer, _gameObjects: unknown[], _deltaX: number, deltaY: number): void {
    if (deltaY < 0) {
      this.zoomIn();
    } else if (deltaY > 0) {
      this.zoomOut();
    }
  }

  /**
   * Handle pointer down for drag start
   */
  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    // Check for pinch zoom (two touches)
    if (this.config.touchEnabled && this.scene.input.pointer1.isDown && this.scene.input.pointer2.isDown) {
      this.isPinching = true;
      this.isDragging = false;
      this.lastPinchDistance = this.getPinchDistance();
      return;
    }

    // Single pointer drag
    this.isDragging = true;
    this.lastPointerPosition = { x: pointer.x, y: pointer.y };
  }

  /**
   * Handle pointer move for pan and pinch
   */
  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    // Handle pinch zoom
    if (this.isPinching && this.config.touchEnabled) {
      const currentDistance = this.getPinchDistance();
      const delta = currentDistance - this.lastPinchDistance;

      if (Math.abs(delta) > 10) {
        if (delta > 0) {
          this.zoomIn();
        } else {
          this.zoomOut();
        }
        this.lastPinchDistance = currentDistance;
      }
      return;
    }

    // Handle drag pan
    if (this.isDragging && pointer.isDown) {
      const dx = pointer.x - this.lastPointerPosition.x;
      const dy = pointer.y - this.lastPointerPosition.y;

      // Invert for natural panning (drag specimen, not viewport)
      this.pan(-dx, -dy);

      this.lastPointerPosition = { x: pointer.x, y: pointer.y };
    }
  }

  /**
   * Handle pointer up to end drag/pinch
   */
  private handlePointerUp(): void {
    this.isDragging = false;
    this.isPinching = false;
  }

  /**
   * Calculate distance between two touch points
   */
  private getPinchDistance(): number {
    const p1 = this.scene.input.pointer1;
    const p2 = this.scene.input.pointer2;

    if (!p1.isDown || !p2.isDown) return 0;

    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Zoom in to the next zoom level
   */
  public zoomIn(): boolean {
    if (this.zoomIndex >= MicroscopeSystem.ZOOM_LEVELS.length - 1) {
      return false;
    }

    this.zoomIndex++;
    this.state.zoomLevel = MicroscopeSystem.ZOOM_LEVELS[this.zoomIndex];

    this.emitZoomEvent('in');
    return true;
  }

  /**
   * Zoom out to the previous zoom level
   */
  public zoomOut(): boolean {
    if (this.zoomIndex <= 0) {
      return false;
    }

    this.zoomIndex--;
    this.state.zoomLevel = MicroscopeSystem.ZOOM_LEVELS[this.zoomIndex];

    this.emitZoomEvent('out');
    return true;
  }

  /**
   * Set zoom directly to a specific level
   */
  public setZoomLevel(level: number): boolean {
    const index = MicroscopeSystem.ZOOM_LEVELS.indexOf(level as typeof MicroscopeSystem.ZOOM_LEVELS[number]);
    if (index === -1) return false;

    const direction = index > this.zoomIndex ? 'in' : 'out';
    this.zoomIndex = index;
    this.state.zoomLevel = level;

    this.emitZoomEvent(direction);
    return true;
  }

  /**
   * Pan the view by delta amounts
   */
  public pan(dx: number, dy: number): void {
    // Scale pan amount by zoom level (lower zoom = larger movements)
    const scale = 1 / Math.sqrt(this.state.zoomLevel);
    const scaledDx = dx * scale;
    const scaledDy = dy * scale;

    let newX = this.state.position.x + scaledDx;
    let newY = this.state.position.y + scaledDy;

    // Clamp to world bounds
    const bounds = this.config.worldBounds;
    newX = Phaser.Math.Clamp(newX, bounds.x, bounds.x + bounds.width);
    newY = Phaser.Math.Clamp(newY, bounds.y, bounds.y + bounds.height);

    const delta = {
      x: newX - this.state.position.x,
      y: newY - this.state.position.y,
    };

    this.state.position = { x: newX, y: newY };

    this.emitPanEvent(delta);
  }

  /**
   * Set position directly
   */
  public setPosition(x: number, y: number): void {
    const bounds = this.config.worldBounds;
    this.state.position = {
      x: Phaser.Math.Clamp(x, bounds.x, bounds.x + bounds.width),
      y: Phaser.Math.Clamp(y, bounds.y, bounds.y + bounds.height),
    };

    this.emitPanEvent({ x: 0, y: 0 });
  }

  /**
   * Get the current zoom threshold for detail visibility
   */
  public getZoomThreshold(): ZoomThreshold {
    const level = this.state.zoomLevel;

    if (level >= 1000) return ZoomThreshold.MOLECULAR;
    if (level >= 400) return ZoomThreshold.ORGANELLE;
    if (level >= 100) return ZoomThreshold.CELL;
    if (level >= 10) return ZoomThreshold.TISSUE;
    return ZoomThreshold.ORGANISM;
  }

  /**
   * Check if organelles should be visible at current zoom
   */
  public organellesVisible(): boolean {
    return this.state.zoomLevel >= ZoomThreshold.ORGANELLE;
  }

  /**
   * Check if cell structure should be visible
   */
  public cellStructureVisible(): boolean {
    return this.state.zoomLevel >= ZoomThreshold.CELL;
  }

  /**
   * Set the stain type
   */
  public setStain(stainType: StainType): void {
    this.state.stainType = stainType;
    this.scene.events.emit('microscope:stain', stainType);
  }

  /**
   * Set scanner active state
   */
  public setScannerActive(active: boolean): void {
    this.state.scannerActive = active;
    this.scene.events.emit('microscope:scanner', active);
  }

  /**
   * Get the visible bounds in world coordinates
   */
  public getVisibleBounds(): Phaser.Geom.Rectangle {
    const camera = this.scene.cameras.main;
    const scale = this.state.zoomLevel;

    // Calculate visible area based on camera size and zoom
    const width = camera.width / scale;
    const height = camera.height / scale;

    return new Phaser.Geom.Rectangle(
      this.state.position.x - width / 2,
      this.state.position.y - height / 2,
      width,
      height
    );
  }

  /**
   * Get the current microscope state
   */
  public getState(): Readonly<MicroscopeState> {
    return { ...this.state };
  }

  /**
   * Get current zoom level
   */
  public getZoomLevel(): number {
    return this.state.zoomLevel;
  }

  /**
   * Get current zoom index
   */
  public getZoomIndex(): number {
    return this.zoomIndex;
  }

  /**
   * Get current position
   */
  public getPosition(): Vector2 {
    return { ...this.state.position };
  }

  /**
   * Emit zoom event
   */
  private emitZoomEvent(direction: 'in' | 'out'): void {
    const event: ZoomEvent = {
      level: this.state.zoomLevel,
      index: this.zoomIndex,
      direction,
      threshold: this.getZoomThreshold(),
    };

    this.scene.events.emit('microscope:zoom', event);
  }

  /**
   * Emit pan event
   */
  private emitPanEvent(delta: Vector2): void {
    const event: PanEvent = {
      position: { ...this.state.position },
      delta,
    };

    this.scene.events.emit('microscope:pan', event);
  }

  /**
   * Clean up event listeners
   */
  public destroy(): void {
    const input = this.scene.input;

    input.off('wheel', this.handleWheel, this);
    input.off('pointerdown', this.handlePointerDown, this);
    input.off('pointermove', this.handlePointerMove, this);
    input.off('pointerup', this.handlePointerUp, this);
    input.off('pointerupoutside', this.handlePointerUp, this);

    if (input.keyboard) {
      input.keyboard.off('keydown-PLUS');
      input.keyboard.off('keydown-MINUS');
      input.keyboard.off('keydown-EQUALS');
    }
  }
}
